import { useEffect, useRef, useState } from 'react'
import { ChevronIcon } from './icons'
import styles from './Select.module.css'

export interface Option {
  value: string
  label: string
  /** Вторая строка: сеть, пояснение. Пусто — строки нет. */
  sub?: string
  /** Цветная метка слева. */
  dot?: string
}

interface Props {
  /** Подпись сверху. Пусто — селект без подписи, как выбор периода. */
  label?: string | undefined
  /** Имя для читалки, когда подписи нет. */
  ariaLabel?: string | undefined
  placeholder: string
  options: Option[]
  value: string | null
  onChange: (value: string) => void
  disabled?: boolean
  /** Текст ошибки под полем. Красная рамка без причины бесполезна. */
  error?: string | null
  /** Серая строка под полем из макета («Комиссия сети: 3 USDT»). */
  description?: string | null
  /**
   * С какой ширины экрана подпись видна. Ниже — остаётся для читалки,
   * но не занимает места: в макете у фильтров на телефоне подписей нет.
   */
  labelFrom?: 768 | 1280
}

/**
 * Выпадающий список из макета.
 *
 * Своя реализация, а не `<select>`: в макете у пункта две строки
 * (монета и сеть) и цветная метка, чего нативный список не умеет
 * ни в одном браузере. Клавиатура при этом обязана работать так же —
 * человек, который платит с клавиатуры, не должен упереться в тупик.
 */
export function Select({
  label, ariaLabel, placeholder, options, value, onChange, disabled, error, description,
  labelFrom,
}: Props) {
  const name = label ?? ariaLabel ?? placeholder
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)

  const selected = options.find((option) => option.value === value) ?? null

  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (option: Option) => {
    onChange(option.value)
    setOpen(false)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return
    if (!open && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
      event.preventDefault()
      setOpen(true)
      setActive(Math.max(0, options.findIndex((o) => o.value === value)))
      return
    }
    if (!open) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((i) => Math.min(i + 1, options.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = options[active]
      if (option) pick(option)
    }
  }

  return (
    <div className={styles.field} ref={boxRef} data-label-from={labelFrom}>
      {label ? <span className={styles.label} id={`lbl-${name}`}>{label}</span> : null}
      <button
        type="button"
        className={error ? `${styles.trigger} ${styles.bad}` : styles.trigger}
        aria-haspopup="listbox"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `err-${name}` : undefined}
        aria-expanded={open}
        /* Имя кнопки — «Валюта USDT», а не просто «Валюта»: без текущего
           значения человек с экранной читалкой не знает, что уже выбрано,
           а шаг здесь необратимый. */
        aria-labelledby={label ? `lbl-${name} val-${name}` : `val-${name}`}
        aria-label={label ? undefined : ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onKeyDown}
      >
        {selected?.dot ? (
          <span className={styles.dot}
                style={{ '--dot': selected.dot } as React.CSSProperties} />
        ) : null}
        <span
          id={`val-${name}`}
          className={selected ? styles.value : `${styles.value} ${styles.placeholder}`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span className={styles.chevron}><ChevronIcon /></span>
      </button>

      {open ? (
        <div className={styles.popover} role="listbox">
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              data-active={index === active}
              className={styles.option}
              onMouseEnter={() => setActive(index)}
              onClick={() => pick(option)}
            >
              {option.dot ? (
                <span className={styles.dot}
                      style={{ '--dot': option.dot } as React.CSSProperties} />
              ) : null}
              <span className={styles.optionBody}>
                <span>{option.label}</span>
                {option.sub ? <span className={styles.optionSub}>{option.sub}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <span className={styles.error} id={`err-${name}`} role="alert">{error}</span>
      ) : null}
      {description && !error ? (
        <span className={styles.description}>{description}</span>
      ) : null}
    </div>
  )
}
